import React, { useContext } from 'react';
import { Button } from 'antd';

import TicketContext from '@totem/components/ticketing/details/ticketContext';
import { Activity } from '@totem/components/ticketing/ticketing';
import { defaultActivity } from '@totem/components/ticketing/utilities';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { Attribute } from '@totem/types/common';
import { isNotNull } from '@totem/utilities/common';

const ASSIGN_ACTIVITY_ID = '00000055f3329dc06bba0005';

const styles = {
  button: {
    fontSize: '36px',
    marginLeft: '0.5em',
  },
};

type Props = {
  buttonProps?: Omit<
    React.ComponentProps<typeof Button>,
    'onClick' | 'disabled' | 'children'
  >;
};

const TakeOwnershipButton = ({ buttonProps }: Props) => {
  const { userProfile } = useContext(UserProfileContext);
  const { ticket, activityConfiguration, onCreateActivity } =
    useContext(TicketContext);

  const ifTicketEligible = () => {
    return (
      ticket.ticket.status !== 'RESOLVED' && ticket.ticket.status !== 'CLOSED'
    );
  };

  const hasActiveAssignments = () => {
    if (isNotNull(ticket)) {
      const assignedParties = ticket.ticket.partiesInvolved.filter(
        (item) =>
          item.status === 'ACTIVE' &&
          (item.type === 'Assignee' ||
            item.type === 'Project Manager' ||
            item.type === 'Customer Assignee'),
      );

      return assignedParties.length > 0;
    }

    return false;
  };

  const hasPermissions = () => {
    const activityConfig = activityConfiguration.find(
      (activity) => activity.id === ASSIGN_ACTIVITY_ID,
    );

    return isNotNull(activityConfig);
  };

  const logAssignActivity = () => {
    const activityConfig = activityConfiguration.find(
      (activity) => activity.id === ASSIGN_ACTIVITY_ID,
    );

    if (!isNotNull(activityConfig) || !isNotNull(userProfile)) {
      return;
    }

    const attributes: Attribute[] = [
      {
        name: 'party-involved-type',
        label: 'Involvement Type',
        value: 'Assignee',
        display: true,
        id: '',
      },
      {
        name: 'party-involved-name',
        label: 'Name',
        value: `${userProfile.firstName} ${userProfile.lastName}`.trim(),
        display: true,
        id: '',
      },
      {
        name: 'party-involved-email',
        label: 'Email',
        value: userProfile.email,
        display: true,
        id: '',
      },
      {
        name: 'party-involved-phone',
        label: 'Phone',
        value: userProfile.phoneNumber ?? '',
        display: true,
        id: '',
      },
    ];

    const activity: Activity = {
      ...defaultActivity,
      type: {
        id: activityConfig.id,
        code: activityConfig.code,
        display: activityConfig.display,
      },
      attributes,
      createdAt: new Date().toISOString(),
    };

    onCreateActivity(activity);
  };

  return (
    <>
      {ifTicketEligible() && hasPermissions() && !hasActiveAssignments() && (
        <div style={styles.button}>
          <Button
            {...(buttonProps as object)}
            type="primary"
            size={'small'}
            onClick={() => logAssignActivity()}
          >
            Take Ownership
          </Button>
        </div>
      )}
    </>
  );
};

export default TakeOwnershipButton;
